import { graphql } from 'src/gql';

import { useLazyQueryWithErrorLogging } from '@savvy/graphql-hooks/useLazyQueryWithErrorLogging';

// Same `userKycStatus.missingFields` signal that drives the "Missing information" banner on the
// account Details page, so the upgrade-custodian gate stays consistent with what the advisor sees
// there. Renamed operation to avoid colliding with the `UserKycStatus` query in details.tsx.
const UpgradeCustodianUserKycStatusQuery = graphql(/* GraphQL */ `
  query UpgradeCustodianUserKycStatus($userId: ID!) {
    userKycStatus(userId: $userId) {
      missingFields
    }
  }
`);

export function useCheckHouseholdKycMissingDetails() {
  const [fetchPrimaryKycStatus] = useLazyQueryWithErrorLogging(UpgradeCustodianUserKycStatusQuery);
  const [fetchCoClientKycStatus] = useLazyQueryWithErrorLogging(UpgradeCustodianUserKycStatusQuery);
  type KycStatusResult = Awaited<ReturnType<typeof fetchPrimaryKycStatus>> | undefined;

  function hasMissingFields(result: PromiseSettledResult<KycStatusResult>) {
    if (result.status === 'rejected') return false;

    return (result.value?.data?.userKycStatus.missingFields.length ?? 0) > 0;
  }

  async function checkHouseholdKycMissingDetails({
    primaryClientUserId,
    coClientUserId,
  }: {
    readonly primaryClientUserId: string | null | undefined;
    readonly coClientUserId: string | null | undefined;
  }) {
    const [primaryResult, coClientResult] = await Promise.allSettled([
      primaryClientUserId ? fetchPrimaryKycStatus({ variables: { userId: primaryClientUserId } }) : undefined,
      coClientUserId ? fetchCoClientKycStatus({ variables: { userId: coClientUserId } }) : undefined,
    ]);

    return hasMissingFields(primaryResult) || hasMissingFields(coClientResult);
  }

  return checkHouseholdKycMissingDetails;
}
